import React, { useState } from 'react';
import AudioPlayer from './AudioPlayer';
import { MultiCallGroup, RecordingRecord } from '../services/smsExportService';

interface Props {
  group: MultiCallGroup;
  index: number;          // 1-based position in the queue
  total: number;          // total groups to resolve
  initialSelected: string[]; // recording s3Keys pre-checked (for re-open)
  onConfirm: (selectedS3Keys: string[]) => void;
  onRemoveEnrollment: () => void;
}

function fmtDuration(sec: number): string {
  if (!sec) return '—';
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function fmtTime(rec: RecordingRecord): string {
  if (rec.callTimestamp) return new Date(rec.callTimestamp).toLocaleString();
  return rec.recordDate || '—';
}

export default function MultiCallModal({ group, index, total, initialSelected, onConfirm, onRemoveEnrollment }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));
  const toggle = (s3Key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(s3Key) ? next.delete(s3Key) : next.add(s3Key);
      return next;
    });

  const { csvRow, candidates } = group;
  const canConfirm = selected.size > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 680, maxWidth: '92vw',
        maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Multiple calls — {index} of {total}</div>
        <h3 style={{ margin: '0 0 4px 0' }}>
          {csvRow.applicantFirstName} {csvRow.applicantLastName}
        </h3>
        <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#555' }}>
          {candidates.length} calls found for confirmation <code>{csvRow.confirmationNumber || '(none)'}</code> on {csvRow.date}.
          Listen and check which call(s) to send. A short call is often a hangup that doesn't need sending.
        </p>

        <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
          {candidates.map((rec) => (
            <div key={rec.s3Key} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <input type="checkbox" checked={selected.has(rec.s3Key)} onChange={() => toggle(rec.s3Key)} />
              <div style={{ flex: 1, fontSize: 12 }}>
                <div><strong>{fmtTime(rec)}</strong> · <span style={{ color: rec.duration < 15 ? '#e65100' : '#2e7d32' }}>{fmtDuration(rec.duration)}</span></div>
                <div style={{ color: '#888' }}>{rec.agentId || '—'} · {rec.direction || '—'}</div>
              </div>
              <AudioPlayer systemId={rec.systemId} archivePath={rec.archivePath || ''} filename={rec.originalFilename} />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button type="button" onClick={onRemoveEnrollment}
            style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 12 }}>
            Remove this enrollment from the batch
          </button>
          <button type="button" disabled={!canConfirm} onClick={() => onConfirm(Array.from(selected))}
            style={{ padding: '8px 20px', border: 'none', borderRadius: 6, fontSize: 13,
              cursor: canConfirm ? 'pointer' : 'not-allowed', background: canConfirm ? '#00bcd4' : '#ccc', color: '#fff' }}>
            Confirm {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
